import { useState } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { Cloud, Check, ArrowRight, Menu, X } from "lucide-react";

export function PricingPage() {
    const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const plans = [
        {
            name: "Free",
            price: 0,
            description: "For hobby projects and testing",
            requests: "10,000",
            features: [
                "Current weather data",
                "3-day forecast",
                "60 requests/minute",
                "Community support",
            ],
            cta: "Get Started",
            popular: false,
        },
        {
            name: "Developer",
            price: billingPeriod === "annual" ? 24 : 29,
            description: "For indie developers and small apps",
            requests: "100,000",
            features: [
                "Everything in Free",
                "7-day forecast",
                "7-day historical data",
                "300 requests/minute",
                "Email support",
            ],
            cta: "Start Free Trial",
            popular: false,
        },
        {
            name: "Pro",
            price: billingPeriod === "annual" ? 79 : 99,
            description: "For growing businesses",
            requests: "500,000",
            features: [
                "Everything in Developer",
                "14-day forecast",
                "30-day historical data",
                "Weather alerts",
                "Air quality data",
                "1,000 requests/minute",
                "Priority support",
            ],
            cta: "Start Free Trial",
            popular: true,
        },
        {
            name: "Scale",
            price: billingPeriod === "annual" ? 239 : 299,
            description: "For high-volume applications",
            requests: "2,000,000",
            features: [
                "Everything in Pro",
                "10,000 requests/minute",
                "99.9% SLA",
                "Dedicated support",
                "Custom integrations",
            ],
            cta: "Contact Sales",
            popular: false,
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            {/* Navbar */}
            <nav className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-slate-200 z-50">
                <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
                    <Link href="/" className="flex items-center gap-2 group">
                        <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-200 group-hover:scale-105 transition-transform">
                            <Cloud className="w-5 h-5 fill-white/20" />
                        </div>
                        <span className="font-bold text-lg text-slate-800">
                            weatherapi<span className="text-sky-600">.dev</span>
                        </span>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-4">
                        <Link href="/docs" className="text-sm text-slate-600 hover:text-slate-800 transition-colors">
                            Docs
                        </Link>
                        <Link
                            href="/login"
                            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                        >
                            Sign In
                        </Link>
                        <Link
                            href="/signup"
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-sky-600 text-white text-sm font-medium rounded-lg hover:opacity-90 shadow-lg shadow-sky-200 transition-all"
                        >
                            Get Started <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2 text-slate-600"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-slate-200 bg-white">
                        <div className="px-6 py-4 space-y-4">
                            <Link href="/docs" className="block text-sm text-slate-600">Docs</Link>
                            <Link href="/login" className="block text-sm text-slate-600">Sign In</Link>
                            <Link href="/signup" className="block w-full text-center px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg">
                                Get Started
                            </Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero */}
            <section className="py-20 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                        Simple, transparent pricing
                    </h1>
                    <p className="text-xl text-slate-600 mb-8">
                        Start free, scale as you grow. No hidden fees.
                    </p>

                    {/* Billing Toggle */}
                    <div className="inline-flex items-center gap-3 bg-slate-100 rounded-full p-1">
                        <button
                            onClick={() => setBillingPeriod("monthly")}
                            className={cn(
                                "px-4 py-2 text-sm font-medium rounded-full transition-colors",
                                billingPeriod === "monthly"
                                    ? "bg-white text-slate-800 shadow-sm"
                                    : "text-slate-600"
                            )}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingPeriod("annual")}
                            className={cn(
                                "px-4 py-2 text-sm font-medium rounded-full transition-colors",
                                billingPeriod === "annual"
                                    ? "bg-white text-slate-800 shadow-sm"
                                    : "text-slate-600"
                            )}
                        >
                            Annual{" "}
                            <span className="text-emerald-600 text-xs font-semibold">-20%</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="pb-20 px-6">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {plans.map((plan) => (
                        <div
                            key={plan.name}
                            className={cn(
                                "bg-white rounded-2xl p-6 transition-all",
                                plan.popular
                                    ? "border-2 border-sky-500 shadow-xl shadow-sky-100 relative"
                                    : "border border-slate-200 hover:border-slate-300 hover:shadow-lg"
                            )}
                        >
                            {plan.popular && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-sky-500 to-sky-600 text-white text-xs font-semibold rounded-full">
                                    Most Popular
                                </span>
                            )}

                            <h3 className="text-xl font-bold text-slate-800">{plan.name}</h3>
                            <p className="text-sm text-slate-500 mt-1 mb-4">{plan.description}</p>

                            <div className="mb-4">
                                <span className="text-4xl font-bold text-slate-800">${plan.price}</span>
                                <span className="text-slate-500">/mo</span>
                            </div>

                            <p className="text-sm text-slate-600 mb-6">
                                <span className="font-semibold">{plan.requests}</span> requests/month
                            </p>

                            <button
                                className={cn(
                                    "w-full py-3 text-sm font-medium rounded-xl transition-all mb-6",
                                    plan.popular
                                        ? "bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-lg shadow-sky-200 hover:opacity-90"
                                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                )}
                            >
                                {plan.cta}
                            </button>

                            <ul className="space-y-3">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-600">
                                        <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-4">
                        {[
                            {
                                q: "Can I switch plans anytime?",
                                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and we'll prorate the billing.",
                            },
                            {
                                q: "What happens if I exceed my quota?",
                                a: "We'll send you an alert at 80% usage. If you exceed your quota, requests will return a 429 error until the next billing cycle or until you upgrade.",
                            },
                            {
                                q: "Do you offer a free trial?",
                                a: "Yes, all paid plans come with a 14-day free trial. No credit card required to start.",
                            },
                            {
                                q: "What payment methods do you accept?",
                                a: "We accept all major credit cards (Visa, Mastercard, Amex) and also support payment via crypto (USDC on Base).",
                            },
                        ].map((faq) => (
                            <div key={faq.q} className="bg-white rounded-xl p-6 border border-slate-200">
                                <h3 className="font-semibold text-slate-800 mb-2">{faq.q}</h3>
                                <p className="text-slate-600 text-sm">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-900 text-slate-400 py-12 px-6">
                <div className="max-w-6xl mx-auto text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-sky-600 rounded-lg flex items-center justify-center text-white">
                            <Cloud className="w-5 h-5 fill-white/20" />
                        </div>
                        <span className="font-semibold text-white">
                            weatherapi<span className="text-sky-400">.dev</span>
                        </span>
                    </div>
                    <p className="text-sm">
                        © 2026 weatherapi.dev. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default PricingPage;
